import { z } from 'zod';
/**
 * Les **réglages de l'organisation** : l'identité de GHP, et les paramètres des deux livrables
 * qu'elle commande — la fiche de contrôle (PDF) et le **cartouche** cartographique, ce cartel
 * imprimé sur une carte exportée (logo, titre du lot, légende des conclusions, échelle, date).
 *
 * ⚠️ **Un enregistrement unique, jamais une liste.** `Organization` du diagramme est un singleton
 * de fait — GHP est seul utilisateur de son back-office, et le multi-organisation est écarté
 * depuis le 06/08/2026. Ce contrat n'a donc **pas d'`id`** : personne ne l'adresse par clé, et en
 * exposer un inviterait à en demander un second.
 *
 * ⚠️ **Ce ne sont pas les préférences d'une personne.** Les notifications et les préférences
 * d'affichage de la maquette appartiennent au compte connecté, pas à l'organisation : autre
 * stockage, autre contrat.
 *
 * ⚠️ **Tout est nullable sauf les bascules, parce que rien n'est encore enregistré.** La table
 * naît vide : tant que personne n'a rien saisi, la lecture rend `DEFAULT_ORGANIZATION_SETTINGS`
 * et les champs de texte de l'écran sont vides. Un `''` à la place d'un `null` dirait
 * « renseigné, et vide », ce qui est faux — et ferait imprimer une raison sociale absente en tête
 * d'un cartouche.
 */
export const organizationSettingsSchema = z.object({
    /** La raison sociale, telle qu'elle s'imprime sur la fiche et sur le cartouche. */
    name: z.string().trim().min(1).nullable(),
    /**
     * Le SIRET, **sans contrôle de format** : la maquette l'affiche espacé
     * (« 512 044 190 00034 »), les courriers le donnent collé, et refuser une saisie encore
     * incomplète perdrait le reste de l'enregistrement (M21). C'est un champ d'en-tête de
     * document, pas une clé.
     */
    siret: z.string().trim().min(1).nullable(),
    /**
     * Le **contact référent** — une personne (`Person`, M15) rattachée à l'organisation, et non une
     * colonne de texte : le diagramme la relie par `contact_id`, et les cinq autres rôles de
     * personnes du modèle passent déjà par cette table.
     *
     * Un seul champ de nom, comme la maquette : `Person.lastName` porte « un nom de famille **ou**
     * une raison sociale », donc « A. Lemoine » aussi bien que « BLANDINEAU IMMOBILIER ». Le
     * découper en nom et prénom obligerait à deviner où couper.
     */
    contactName: z.string().trim().min(1).nullable(),
    /**
     * L'adresse du contact. Format exigé ici, contrairement au SIRET : c'est une adresse
     * d'**envoi** — elle s'imprime en pied de document et sert à répondre —, et une adresse fautive
     * ne se voit qu'au message qui ne revient jamais.
     */
    contactEmail: z.email().nullable(),
    /** Fiche de contrôle (PDF) : les photos du relevé sont annexées au document. */
    reportIncludePhotos: z.boolean(),
    /** Fiche de contrôle (PDF) : filigrane « Brouillon » tant que le contrôle n'est pas validé. */
    reportDraftWatermark: z.boolean(),
    /**
     * L'échelle du cartouche — du **texte libre**, « 1 : 2 500 » dans la maquette, et le diagramme
     * la tient en `text` là où il sait énumérer ailleurs. Il n'existe pas de liste d'échelles
     * arrêtée : en figer une ici ferait refuser la première carte au 1 : 500.
     */
    mapDefaultScale: z.string().trim().min(1).nullable(),
    /** Le format du cartouche — « A3 paysage » dans la maquette. Texte libre, comme l'échelle. */
    mapDefaultFormat: z.string().trim().min(1).nullable(),
    /** Cartouche : le logo de la commune s'imprime à côté de celui de GHP, quand on l'a. */
    mapIncludeMunicipalityLogo: z.boolean(),
    /**
     * « Autoriser les agents à rajouter des pièces jointes après avoir transmis le contrôle »
     * ([M35](../../../../docs/MODELE-DONNEES.md#m35), tranché le 21/09/2026).
     *
     * ⚠️ **Toute l'organisation, jamais par équipe ni par agent.** C'est une règle de tenue de
     * dossier — ce que GHP accepte de voir arriver sur un contrôle qu'il est en train de valider —,
     * pas une permission qui se délègue. Un périmètre par équipe ferait deux jeux de règles pour le
     * même client final, et rien à l'écran ne dirait laquelle s'applique.
     *
     * ⚠️ **Ce n'est pas un réglage de document**, contrairement aux trois bascules au-dessus : il
     * commande le **verrou de l'éditeur terrain** et, derrière lui, ce que l'API de synchro accepte.
     * Les deux doivent lire la même valeur — un verrou d'écran seul se contourne avec une tablette
     * en retard d'un déploiement.
     */
    allowAttachmentsAfterSubmission: z.boolean(),
    /**
     * « Mot de passe oublié » par e-mail (D3). Coupé, l'écran renvoie vers un administrateur, qui
     * tire un nouveau mot de passe depuis « Utilisateurs & rôles » — et l'API n'envoie plus rien :
     * un interrupteur d'écran seul se contourne d'un `curl`.
     */
    passwordResetByEmail: z.boolean(),
});
/**
 * Ce que rend la lecture **tant que rien n'a été enregistré**, et ce que porte la ligne créée au
 * premier enregistrement pour les champs qu'il ne touche pas.
 *
 * ⚠️ **Les défauts vivent ici et nulle part ailleurs**, en particulier pas en `DEFAULT` de
 * colonne : la lecture sans ligne et l'écriture de la première ligne les prendraient alors à deux
 * endroits, et la prochaine bascule ajoutée les ferait diverger sans qu'aucun test ne le voie.
 * Les trois états de bascule sont ceux de la maquette validée.
 *
 * ⚠️ **`allowAttachmentsAfterSubmission` est à `false` parce que c'est le comportement actuel**,
 * pas par prudence : la tablette verrouille la fiche à la transmission (`enqueueSubmission` pose
 * `lockedAt`) et seul un renvoi du bureau la rouvre (`reopenReturned`). Un défaut à `true`
 * ouvrirait, au premier déploiement, un geste que personne n'a encore demandé — et il n'existe
 * pas d'écran pour le refermer avant que 3.7 ne livre l'interrupteur.
 */
export const DEFAULT_ORGANIZATION_SETTINGS = {
    name: null,
    siret: null,
    contactName: null,
    contactEmail: null,
    reportIncludePhotos: true,
    reportDraftWatermark: false,
    mapDefaultScale: null,
    mapDefaultFormat: null,
    mapIncludeMunicipalityLogo: true,
    allowAttachmentsAfterSubmission: false,
    // Coupé (décision du développeur, 26/10/2026) : aucun lien ne part tant qu'un administrateur
    // ne l'a pas allumé — la réinitialisation par un admin reste le chemin par défaut.
    passwordResetByEmail: false,
};
/**
 * Ce qu'un enregistrement transporte : **ce qui a changé**, rien d'autre. La maquette n'a qu'un
 * bouton « Enregistrer » pour tout l'écran, et il reste désarmé tant que rien ne bouge — ce corps
 * n'est donc jamais vide.
 *
 * ⚠️ **`strictObject`** : un champ inconnu — la bascule « QGIS Server », qui n'a pas de colonne
 * tant que D6 n'est pas tranchée, ou un `id` qu'on croirait devoir fournir — serait autrement
 * retiré en silence, et l'appelant croirait l'avoir enregistré.
 *
 * ⚠️ **Vider un champ s'écrit `null`, jamais `''`.** Les deux se ressemblent dans un formulaire et
 * ne disent pas la même chose une fois stockés : `''` s'imprimerait comme une valeur renseignée.
 * L'écran convertit donc son champ vidé en `null`.
 *
 * ⚠️ **Au moins un champ**, comme `updateUserSchema` : un corps vide n'est pas une saisie douteuse
 * à enregistrer (M21) mais un appel qui ne demande rien — l'accepter masquerait le bug de
 * l'appelant, et Drizzle refuse de toute façon un `set({})`.
 */
export const updateOrganizationSettingsSchema = z
    .strictObject(organizationSettingsSchema.partial().shape)
    .refine((patch) => Object.keys(patch).length > 0, { error: 'aucun réglage à modifier' });
//# sourceMappingURL=settings.js.map